import React, { useState, useEffect, useRef } from "react"
import type { Size } from "./useResizeObserver"

interface ResizeObserverEntry {
  contentRect: DOMRectReadOnly
  target: Element
}

/**
 * Web implementation of useResizeObserver using ResizeObserver API
 */
export function useResizeObserver<T extends HTMLElement = HTMLElement>(
  initialSize: Size = { width: 0, height: 0 },
): [React.RefObject<T>, Size] {
  const ref = useRef<T>(null)
  const [size, setSize] = useState<Size>(initialSize)

  useEffect(() => {
    if (!ref.current) return

    const resizeObserver = new ResizeObserver(
      (entries: ResizeObserverEntry[]) => {
        if (!Array.isArray(entries) || !entries.length) return

        const entry = entries[0]
        const { width, height } = entry.contentRect

        setSize({ width, height })
      },
    )

    resizeObserver.observe(ref.current)

    return () => {
      resizeObserver.disconnect()
    }
  }, [])

  return [ref, size]
}
